import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useBranches } from "@/hooks/useBranches";
import { useAuditLogs, EMPTY_AUDIT_FILTERS, type AuditFilters } from "@/hooks/useAuditLogs";
import { formatDateTime } from "@/lib/format";
import type { AuditLog } from "@/types";
import {
  ACTION_OPTIONS,
  ENTITY_OPTIONS,
  getActionMeta,
  getEntityLabel,
  shortId,
} from "@/pages/admin/audit/auditMeta";
import AuditDetailModal from "@/pages/admin/audit/components/AuditDetailModal";

const PAGE_SIZE = 25;

const TONE_CLASS: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-800",
  secondary: "bg-secondary-100 text-secondary-800",
};

export default function AdminAuditPage() {
  const { profile, isAdmin, hasPermission } = useAuth();
  const { branches } = useBranches();

  const [filters, setFilters] = useState<AuditFilters>(EMPTY_AUDIT_FILTERS);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<AuditLog | null>(null);

  const { logs, total, stats, loading, error, reload } = useAuditLogs(filters, page, PAGE_SIZE);

  useEffect(() => {
    if (profile?.branch_id) {
      setFilters((prev) => (prev.branchId ? prev : { ...prev, branchId: profile.branch_id as string }));
    }
  }, [profile?.branch_id]);

  useEffect(() => {
    setPage(0);
  }, [filters.search, filters.action, filters.entity, filters.branchId]);

  const branchMap = useMemo(() => {
    const map = new Map<string, string>();
    branches.forEach((branch) => map.set(branch.id, branch.name));
    return map;
  }, [branches]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const canView = isAdmin || hasPermission("audit.view");

  const updateFilter = <K extends keyof AuditFilters>(key: K, value: AuditFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const hasFilters =
    Boolean(filters.search) ||
    Boolean(filters.action) ||
    Boolean(filters.entity) ||
    Boolean(filters.branchId && filters.branchId !== profile?.branch_id);

  const statCards = [
    {
      key: "total",
      label: "Registros totales",
      value: stats.total.toLocaleString("es-DO"),
      icon: "ri-file-list-3-line",
      tone: "primary",
    },
    {
      key: "today",
      label: "Actividad de hoy",
      value: stats.today.toLocaleString("es-DO"),
      icon: "ri-calendar-check-line",
      tone: "accent",
    },
    {
      key: "users",
      label: "Usuarios activos",
      value: stats.users.toLocaleString("es-DO"),
      icon: "ri-user-3-line",
      tone: "secondary",
    },
    {
      key: "actions",
      label: "Tipos de acción",
      value: stats.actions.toLocaleString("es-DO"),
      icon: "ri-shield-check-line",
      tone: "secondary",
    },
  ];

  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-background-200 bg-background-100 px-4 py-16 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-lock-2-line text-2xl"></i>
        </span>
        <p className="text-sm font-semibold text-foreground-800">Acceso restringido</p>
        <p className="max-w-sm text-xs text-foreground-500">
          Tu usuario no tiene permiso para consultar la auditoría del sistema.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Auditoría</h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            Registro completo de acciones importantes: quién, qué, cuándo y dónde.
          </p>
        </div>
        <button
          type="button"
          onClick={reload}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
        >
          <i className="ri-refresh-line text-base"></i>
          Actualizar
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {statCards.map((card) => (
          <div
            key={card.key}
            className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4"
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${TONE_CLASS[card.tone]}`}>
              <i className={`${card.icon} text-lg`}></i>
            </span>
            <div className="min-w-0">
              <p className="text-xs font-medium text-foreground-500">{card.label}</p>
              <p className="font-heading mt-0.5 truncate text-lg font-bold text-foreground-950">
                {loading && stats.total === 0 ? "—" : card.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      <section className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative w-full lg:max-w-xs">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              type="text"
              value={filters.search}
              onChange={(event) => updateFilter("search", event.target.value)}
              placeholder="Buscar usuario, acción o referencia..."
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:flex lg:flex-1">
            <select
              value={filters.action}
              onChange={(event) => updateFilter("action", event.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm font-medium text-foreground-800 outline-none focus:border-primary-400 lg:flex-1"
            >
              <option value="">Todas las acciones</option>
              {ACTION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={filters.entity}
              onChange={(event) => updateFilter("entity", event.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm font-medium text-foreground-800 outline-none focus:border-primary-400 lg:flex-1"
            >
              <option value="">Todas las entidades</option>
              {ENTITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {branches.length > 1 ? (
              <select
                value={filters.branchId}
                onChange={(event) => updateFilter("branchId", event.target.value)}
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm font-medium text-foreground-800 outline-none focus:border-primary-400 lg:flex-1"
              >
                <option value="">Todas las sucursales</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
          {hasFilters ? (
            <button
              type="button"
              onClick={() => setFilters(EMPTY_AUDIT_FILTERS)}
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-filter-off-line text-base"></i>
              Limpiar
            </button>
          ) : null}
        </div>
      </section>

      {error ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line text-base"></i>
            {error}
          </span>
          <button
            type="button"
            onClick={reload}
            className="whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-primary-700"
          >
            Reintentar
          </button>
        </div>
      ) : null}

      <section className="flex flex-col rounded-lg border border-background-200 bg-background-100">
        <div className="flex items-center justify-between gap-3 border-b border-background-200 px-4 py-3.5">
          <h2 className="font-heading text-base font-semibold text-foreground-950">Registro de actividad</h2>
          <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-semibold text-secondary-800">
            {total.toLocaleString("es-DO")} registro(s)
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-3 py-16 text-sm text-foreground-500">
            <i className="ri-loader-4-line animate-spin text-lg"></i>
            Cargando auditoría...
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-4 py-16 text-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-history-line text-xl"></i>
            </span>
            <p className="text-sm font-medium text-foreground-700">Sin registros</p>
            <p className="max-w-sm text-xs text-foreground-500">
              No hay acciones que coincidan con los filtros seleccionados.
            </p>
          </div>
        ) : (
          <>
            <div className="hidden overflow-x-auto lg:block">
              <table className="w-full min-w-[860px] border-collapse">
                <thead>
                  <tr className="border-b border-background-200 text-left text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
                    <th className="px-4 py-3">Fecha y hora</th>
                    <th className="px-4 py-3">Usuario</th>
                    <th className="px-4 py-3">Acción</th>
                    <th className="px-4 py-3">Entidad</th>
                    <th className="px-4 py-3">Referencia</th>
                    <th className="px-4 py-3">Sucursal</th>
                    <th className="px-4 py-3 text-right">Detalle</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200">
                  {logs.map((log) => {
                    const meta = getActionMeta(log.action);
                    return (
                      <tr key={log.id} className="transition-colors hover:bg-background-50">
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-600">
                          {formatDateTime(log.created_at)}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-foreground-900">
                          {log.user_name || "Sistema"}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${meta.tone}`}>
                            <i className={`${meta.icon} text-sm`}></i>
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground-700">{getEntityLabel(log.entity)}</td>
                        <td className="px-4 py-3 font-mono text-xs text-foreground-500">{shortId(log.entity_id)}</td>
                        <td className="px-4 py-3 text-sm text-foreground-700">
                          {log.branch_id ? branchMap.get(log.branch_id) ?? "—" : "—"}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => setSelected(log)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:bg-background-200 hover:text-foreground-900"
                            aria-label="Ver detalle"
                          >
                            <i className="ri-eye-line text-base"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col divide-y divide-background-200 lg:hidden">
              {logs.map((log) => {
                const meta = getActionMeta(log.action);
                return (
                  <button
                    key={log.id}
                    type="button"
                    onClick={() => setSelected(log)}
                    className="flex flex-col gap-2 p-4 text-left transition-colors hover:bg-background-50"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${meta.tone}`}>
                        <i className={`${meta.icon} text-sm`}></i>
                        {meta.label}
                      </span>
                      <span className="text-[11px] text-foreground-500">{formatDateTime(log.created_at)}</span>
                    </div>
                    <p className="text-sm font-medium text-foreground-900">{getEntityLabel(log.entity)}</p>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1.5">
                        <i className="ri-user-3-line text-sm"></i>
                        {log.user_name || "Sistema"}
                      </span>
                      <span className="truncate font-mono">{shortId(log.entity_id)}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col items-center justify-between gap-3 border-t border-background-200 px-4 py-3.5 sm:flex-row">
              <p className="text-xs text-foreground-500">
                Página {page + 1} de {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  disabled={page === 0}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <i className="ri-arrow-left-s-line text-base"></i>
                  Anterior
                </button>
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                  disabled={page >= totalPages - 1}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Siguiente
                  <i className="ri-arrow-right-s-line text-base"></i>
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      <AuditDetailModal
        log={selected}
        branchName={selected?.branch_id ? branchMap.get(selected.branch_id) ?? null : null}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}